import { useNavigate } from 'react-router-dom';
import { Leaf, Brain, Zap, Search, Globe, ArrowRight, LogIn, Quote, HelpCircle, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { TodayTasks } from '@/components/home/TodayTasks';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    {
      icon: Brain,
      title: 'Processamento Neural',
      description: 'Tecnologia de ponta em raciocínio visual e identificação de padrões botânicos.',
    },
    {
      icon: Zap,
      title: 'Diagnóstico Instantâneo',
      description: 'Reconhecimento de sintomas, pragas e espécies em milissegundos.',
    },
    {
      icon: Search,
      title: 'Análise Multimodal',
      description: 'Identificação completa de deficiências minerais e necessidades de luz através de fotos.',
    },
    {
      icon: Globe,
      title: 'Interface Progressiva',
      description: 'Web-app ultra-rápido. Sem instalações, otimizado para qualquer navegador.',
    },
  ];

  const faqs = [
    {
      question: 'O VerdeIA é confiável?',
      answer: 'Sim, utilizamos modelos líderes em benchmarks internacionais de visão computacional, garantindo altíssima precisão.',
    },
    {
      question: 'Preciso instalar algum aplicativo?',
      answer: 'Não, o VerdeIA é um web-app que funciona instantaneamente no seu navegador.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      {user ? (
        <MobileHeader showLogo />
      ) : (
        <header className="sticky top-0 z-50 h-14 flex items-center justify-between px-4 bg-background/95 backdrop-blur-sm border-b border-border safe-area-pt">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <Leaf className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-display font-semibold">VerdeIA</span>
          </div>
          <Button onClick={() => navigate('/auth')} variant="outline" size="sm" className="h-9 press-scale">
            <LogIn className="mr-2 h-4 w-4" />
            Entrar
          </Button>
        </header>
      )}

      {user ? (
        // === VERSÃO LOGADO: Simplificada ===
        <>
          <TodayTasks />
          
          {/* Ações Rápidas */}
          <section className="px-4 py-6">
            <div className="max-w-lg mx-auto flex flex-col gap-3">
              <Button
                size="lg"
                onClick={() => navigate('/identify')}
                className="w-full h-12 gradient-nature text-primary-foreground press-scale touch-target"
              >
                <Brain className="mr-2 h-5 w-5" />
                Analisar minha planta agora
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/collection')}
                className="w-full h-12 press-scale touch-target"
              >
                <Search className="mr-2 h-5 w-5" />
                Minha Coleção
              </Button>
            </div>
          </section>
        </>
      ) : (
        // === VERSÃO NÃO LOGADO: Completa ===
        <>
          {/* Hero - Mobile optimized */}
          <section className="relative overflow-hidden">
            <div className="absolute inset-0 leaf-pattern opacity-30" />
            <div className="px-4 py-8 sm:py-12 md:py-20 relative">
              <div className="max-w-lg mx-auto text-center">
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-full text-xs font-medium mb-4">
                  <Brain className="h-3 w-3" />
                  Powered by IA
                </div>
                
                <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4 font-display leading-tight">
                  <span className="text-primary">VerdeIA</span>: A maneira mais fácil de cuidar do seu jardim.
                </h1>
                
                <p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-sm mx-auto">
                  Identifique espécies e diagnostique a saúde das suas plantas com o que há de mais avançado em visão computacional. Ciência de dados aplicada ao seu jardim.
                </p>

                <div className="flex flex-col gap-3">
                  <Button
                    size="lg"
                    onClick={() => navigate('/auth')}
                    className="w-full h-12 gradient-nature text-primary-foreground press-scale touch-target"
                  >
                    <Brain className="mr-2 h-5 w-5" />
                    Analisar minha planta agora
                  </Button>
                </div>
              </div>
            </div>

            {/* Decorative elements - smaller on mobile */}
            <div className="absolute -bottom-10 -left-10 w-32 sm:w-64 h-32 sm:h-64 bg-primary/10 rounded-full blur-3xl" />
            <div className="absolute -top-10 -right-10 w-32 sm:w-64 h-32 sm:h-64 bg-accent/10 rounded-full blur-3xl" />
          </section>

          {/* Benefits Section - NEW */}
          <section className="py-8 sm:py-12 md:py-16 px-4 bg-muted/30">
            <div className="max-w-lg mx-auto text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-4 font-display">
                O conhecimento de um especialista na palma da sua mão.
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
                Para entusiastas do cultivo e profissionais que buscam decisões baseadas em dados. O VerdeIA é para quem exige precisão técnica e não se contenta com suposições.
              </p>
            </div>
          </section>

          {/* Features - Horizontal scroll on mobile */}
          <section className="py-8 sm:py-12 md:py-20">
            <div className="px-4">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-center text-foreground mb-6 sm:mb-8 font-display">
                Tecnologia de ponta
              </h2>
              
              {/* Mobile: horizontal scroll, Desktop: grid */}
              <div className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory -mx-4 px-4 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0 max-w-5xl sm:mx-auto">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 w-[75%] xs:w-[60%] sm:w-auto snap-start text-center p-4 sm:p-6 rounded-2xl bg-card glass-card animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full gradient-nature flex items-center justify-center mx-auto mb-3">
                      <feature.icon className="h-6 w-6 sm:h-7 sm:w-7 text-primary-foreground" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1 text-sm sm:text-base">{feature.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Context Section - NEW */}
          <section className="py-8 sm:py-12 md:py-16 px-4 bg-muted/30">
            <div className="max-w-lg mx-auto text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-4 font-display">
                <span className="text-primary">VerdeIA</span>: Ciência no lugar do 'achismo'.
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
                Você valoriza a eficiência e ferramentas de alta performance. O VerdeIA foi construído para entregar respostas rápidas, precisas e uma interface que respeita sua inteligência.
              </p>
            </div>
          </section>

          {/* Testimonials Section - NEW */}
          <section className="py-8 sm:py-12 md:py-16 px-4">
            <div className="max-w-lg mx-auto">
              <div className="relative p-6 sm:p-8 rounded-2xl bg-card glass-card">
                <Quote className="absolute top-4 left-4 h-8 w-8 text-primary/20" />
                <blockquote className="text-center">
                  <p className="text-sm sm:text-base text-foreground italic mb-4 pt-4">
                    "A precisão da tecnologia VerdeIA para identificar doenças foliares é impressionante. Mudou minha forma de gerir meu jardim."
                  </p>
                  <footer className="text-xs sm:text-sm text-muted-foreground font-medium">
                    — Julio V., Paisagista
                  </footer>
                </blockquote>
              </div>
            </div>
          </section>

          {/* FAQ Section - NEW */}
          <section className="py-8 sm:py-12 md:py-16 px-4 bg-muted/30">
            <div className="max-w-lg mx-auto">
              <div className="flex items-center justify-center gap-2 mb-6">
                <HelpCircle className="h-5 w-5 text-primary" />
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-foreground font-display">
                  Perguntas Frequentes
                </h2>
              </div>
              
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-border">
                    <AccordionTrigger className="text-sm sm:text-base text-foreground hover:text-primary text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>

          {/* CTA - More compact on mobile */}
          <section className="py-8 sm:py-12 md:py-20 px-4">
            <div className="max-w-sm mx-auto p-6 sm:p-8 rounded-2xl sm:rounded-3xl gradient-nature text-primary-foreground">
              <Leaf className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-3 sm:mb-4" />
              <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4 font-display text-center">
                Comece agora
              </h2>
              <p className="mb-4 sm:mb-6 opacity-90 text-xs sm:text-sm text-center">
                Compromisso VerdeIA: Nossa plataforma utiliza sempre os modelos de IA mais avançados e estáveis disponíveis no mercado.
              </p>
              <Button
                size="lg"
                variant="secondary"
                onClick={() => navigate('/auth')}
                className="w-full h-12 press-scale touch-target"
              >
                Começar agora com VerdeIA
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </section>
        </>
      )}

      {/* Footer - Add padding for bottom nav */}
      <footer className={`border-t border-border py-6 sm:py-8 ${user ? 'pb-24' : ''}`}>
        <div className="px-4 text-center text-xs sm:text-sm text-muted-foreground">
          <p>© 2025 VerdeIA. Feito com 💚 para amantes de plantas.</p>
        </div>
      </footer>

      {/* Bottom Navigation - Only for logged in users */}
      {user && <BottomNavigation />}
    </div>
  );
}
